import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import type { FotoResponse } from '@/features/analisis/common/types';

import type { EditarCaptionVars, EliminarFotoVars, FotoTemporal, UseEditarCaptionFn, UseEliminarFotoFn } from '../fotosTypes';

type PersistidaProps = {
  tipo: 'persistida';
  foto: FotoResponse;
  uidProtocolo: string;
  uidAnalisis: string;
  useEditarCaption: UseEditarCaptionFn;
  useEliminarFoto: UseEliminarFotoFn;
  disabled?: boolean;
};

type TemporalProps = {
  tipo: 'temporal';
  foto: FotoTemporal;
  onFotosTemporalesChange: React.Dispatch<React.SetStateAction<FotoTemporal[]>>;
  onRetry: (localId: string, file: File) => void;
  disabled?: boolean;
};

type FotoCardProps = (PersistidaProps | TemporalProps) & {
  onLightbox: (url: string) => void;
};

function CaptionPersistida({
  foto,
  uidProtocolo,
  uidAnalisis,
  useEditarCaption,
  disabled,
}: {
  foto: FotoResponse;
  uidProtocolo: string;
  uidAnalisis: string;
  useEditarCaption: UseEditarCaptionFn;
  disabled?: boolean;
}): JSX.Element {
  const editarMutation = useEditarCaption();
  const [value, setValue] = useState(foto.caption ?? '');
  const [savedOk, setSavedOk] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset when foto changes (e.g. after external update)
  useEffect(() => {
    setValue(foto.caption ?? '');
    setSavedOk(false);
  }, [foto.caption]);

  function handleChange(v: string): void {
    setValue(v);
    setSavedOk(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const vars: EditarCaptionVars = {
        uidProtocolo,
        uidAnalisis,
        uid_foto: foto.uid_foto,
        caption: v.trim() || null,
      };
      editarMutation.mutate(vars, {
        onSuccess: () => setSavedOk(true),
      });
    }, 1500);
  }

  return (
    <TextField
      size="small"
      placeholder="Caption..."
      fullWidth
      value={value}
      onChange={(e) => handleChange(e.target.value)}
      disabled={disabled || editarMutation.isPending}
      InputProps={{
        endAdornment: savedOk ? (
          <InputAdornment position="end">
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
          </InputAdornment>
        ) : undefined,
      }}
      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
    />
  );
}

function CaptionTemporal({
  foto,
  onFotosTemporalesChange,
  disabled,
}: {
  foto: FotoTemporal;
  onFotosTemporalesChange: React.Dispatch<React.SetStateAction<FotoTemporal[]>>;
  disabled?: boolean;
}): JSX.Element {
  function handleChange(v: string): void {
    onFotosTemporalesChange((prev) =>
      prev.map((f) =>
        f.localId === foto.localId ? { ...f, caption: v || null } : f,
      ),
    );
  }

  return (
    <TextField
      size="small"
      placeholder="Caption..."
      fullWidth
      value={foto.caption ?? ''}
      onChange={(e) => handleChange(e.target.value)}
      disabled={disabled || foto.uploadStatus === 'uploading'}
      sx={{ '& .MuiInputBase-input': { fontSize: 12, py: 0.5 } }}
    />
  );
}

function EliminarFotoPersistidaDialog({
  open,
  onClose,
  useEliminarFoto,
  uidProtocolo,
  uidAnalisis,
  uid_foto,
}: {
  open: boolean;
  onClose: () => void;
  useEliminarFoto: UseEliminarFotoFn;
  uidProtocolo: string;
  uidAnalisis: string;
  uid_foto: string;
}): JSX.Element {
  const eliminarMutation = useEliminarFoto();

  function handleConfirm(): void {
    onClose();
    const vars: EliminarFotoVars = { uidProtocolo, uidAnalisis, uid_foto };
    eliminarMutation.mutate(vars);
  }

  return (
    <Dialog open={open} maxWidth="xs" onClose={onClose}>
      <DialogTitle>Eliminar foto</DialogTitle>
      <DialogContent>
        <DialogContentText>¿Eliminar esta foto? Esta acción es irreversible.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button color="error" onClick={handleConfirm}>
          Eliminar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function FotoCard(props: FotoCardProps): JSX.Element {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const imageUrl =
    props.tipo === 'persistida' ? props.foto.presigned_url : props.foto.blobUrl;

  const isUploading = props.tipo === 'temporal' && props.foto.uploadStatus === 'uploading';
  const isError = props.tipo === 'temporal' && props.foto.uploadStatus === 'error';

  function handleDelete(): void {
    if (props.tipo === 'temporal') {
      props.onFotosTemporalesChange((prev) =>
        prev.filter((f) => f.localId !== props.foto.localId),
      );
    } else {
      setConfirmDelete(true);
    }
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: isError ? 'error.main' : 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Image area */}
      <Box
        sx={{
          position: 'relative',
          aspectRatio: '4/3',
          bgcolor: 'grey.100',
          cursor: imageUrl ? 'pointer' : 'default',
        }}
        onClick={() => imageUrl && props.onLightbox(imageUrl)}
      >
        {imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt="Foto análisis"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.200' }} />
        )}

        {/* Uploading overlay */}
        {isUploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        {/* Error overlay */}
        {isError && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <ErrorOutlineIcon color="error" />
            <Typography variant="caption" color="error">
              Error al subir
            </Typography>
            {props.tipo === 'temporal' && (
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (props.tipo === 'temporal') {
                    props.onRetry(props.foto.localId, props.foto.file);
                  }
                }}
              >
                Reintentar
              </Button>
            )}
          </Box>
        )}
      </Box>

      {/* Caption + actions */}
      <Box sx={{ p: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {props.tipo === 'persistida' ? (
            <CaptionPersistida
              foto={props.foto}
              uidProtocolo={props.uidProtocolo}
              uidAnalisis={props.uidAnalisis}
              useEditarCaption={props.useEditarCaption}
              disabled={props.disabled}
            />
          ) : (
            <CaptionTemporal
              foto={props.foto}
              onFotosTemporalesChange={props.onFotosTemporalesChange}
              disabled={props.disabled}
            />
          )}
        </Box>
        <Tooltip title="Eliminar foto">
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={handleDelete}
              disabled={props.disabled || isUploading}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Delete confirmation (persistida only) */}
      {props.tipo === 'persistida' && (
        <EliminarFotoPersistidaDialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          useEliminarFoto={props.useEliminarFoto}
          uidProtocolo={props.uidProtocolo}
          uidAnalisis={props.uidAnalisis}
          uid_foto={props.foto.uid_foto}
        />
      )}
    </Box>
  );
}
